import { useWallet } from '@solana/wallet-adapter-react';
import { pipe } from 'fp-ts/lib/function';
import { chainW, mapLeft, TaskEither } from 'fp-ts/lib/TaskEither';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useArweaveUpload } from '../../hooks/useArweaveUpload';
import {
  usePreviewFileHandler,
  useSingleFileHandler,
} from '../../hooks/useDappFileHandlers';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { errorToLeft } from '../../lib/errorToLeft';
import { TypedError } from '../../lib/typedError';
import { formatMintingErrorMessage } from '../../utils/errorHandling';
import { captureAndReportError } from '../../utils/errorReporting';
import { ensureHttpsUrl } from '../../utils/nftMetadataHelpers';
import { mintAppNft } from '../../utils/nftMinting/appNftMinting';
import { uploadPortalMediaFile } from '../../utils/portalMediaUpload';
import { PUBLISHER_FROZEN_ACTION_TOOLTIP } from '../../utils/publisherFreeze';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { AppNftMintModal } from '../AppNftMintModal';
import { PublisherFrozenBanner } from '../PublisherFrozenBanner';
import {
  FormState,
  MAX_DAPP_NAME_LENGTH,
  NewDappDataType,
  NewDappForm,
} from './NewDappForm';
import { NewDappPageHeader } from './NewDappPageHeader';
export function NewDappPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const wallet = useWallet();
  const { activePublisher } = usePublisher();
  const { uploadAppNftData } = useArweaveUpload();

  usePageTitle('New dApp | Publishing Portal');

  const [formData, setFormData] = useState<FormState>({
    dappName: '',
    packageName: '',
    subtitle: '',
    description: '',
    dappIcon: null,
    banner: null,
    dappPreviews: Array(4).fill(null),
    languages: [],
    availableCountries: [],
    availableCountriesMode: 'include',
    appWebsite: '',
    contactEmail: '',
    supportEmail: '',
    tokenDistribution: false,
    walletAddress: '',
    licenseUrl: '',
    privacyPolicyUrl: '',
    editorsChoiceHeadline: '',
    editorsChoiceTag: '',
    editorsChoiceGraphic: null,
  });

  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showAppNftModal, setShowAppNftModal] = useState(false);
  const [mintingAppNft, setMintingAppNft] = useState(false);
  const publisherVerification = getPublisherVerificationGate({
    status: activePublisher?.kybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const [createdDappData, setCreatedDappData] = useState<{
    id: string;
    name: string;
    iconUrl?: string;
    website?: string;
    androidPackage: string;
    publisherAddress: string;
  } | null>(null);

  useEffect(() => {
    const handleFormChange = () => {
      if (!isFormTouched) {
        setIsFormTouched(true);
      }
    };

    const formElement = document.getElementById('dapp-form');
    if (formElement) {
      formElement.addEventListener('input', handleFormChange);
      formElement.addEventListener('change', handleFormChange);
      return () => {
        formElement.removeEventListener('input', handleFormChange);
        formElement.removeEventListener('change', handleFormChange);
      };
    }
  }, [isFormTouched]);

  const uploadFile = async (
    file: File,
    folder: 'avatars' | 'logos' | 'banners' | 'previews' | 'documents',
  ): Promise<string> => {
    console.log('[uploadFile] Starting upload for file:', {
      name: file.name,
      type: file.type,
      size: file.size,
      folder,
    });
    const publicUrl = await uploadPortalMediaFile({
      trpc,
      file,
      folder,
      fileType:
        folder === 'logos'
          ? 'AppIcon'
          : folder === 'banners'
            ? 'ReleaseBanner'
            : 'ReleaseScreenshot',
      mode: folder === 'previews' ? 'media' : 'image',
    });

    console.log(
      '[uploadFile] Upload successful, returning publicUrl:',
      publicUrl,
    );
    return publicUrl;
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: unknown } },
  ) => {
    const { name, value } = e.target;
    const type = 'type' in e.target ? e.target.type : undefined;

    if (
      (name === 'languages' || name === 'availableCountries') &&
      Array.isArray(value)
    ) {
      setFormData((prev) => ({ ...prev, [name]: value as string[] }));
    } else if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      let limitedValue = value as string;
      if (name === 'dappName' && limitedValue.length > MAX_DAPP_NAME_LENGTH) {
        limitedValue = limitedValue.slice(0, MAX_DAPP_NAME_LENGTH);
      } else if (name === 'editorsChoiceHeadline' && limitedValue.length > 50) {
        limitedValue = limitedValue.slice(0, 50);
      } else if (name === 'editorsChoiceTag' && limitedValue.length > 12) {
        limitedValue = limitedValue.slice(0, 12);
      }
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
    }
  };

  const { handlePreviewFileChange, previewDimensions } = usePreviewFileHandler({
    formPreviews: formData.dappPreviews,
    onFileChange: (index, file) => {
      setFormData((prev) => {
        const newPreviews = [...prev.dappPreviews];
        newPreviews[index] = file;
        return { ...prev, dappPreviews: newPreviews };
      });
    },
    onFormTouched: () => setIsFormTouched(true),
  });

  const handleAddPreviewSlot = () => {
    setFormData((prev) => ({
      ...prev,
      dappPreviews: [...prev.dappPreviews, null],
    }));
  };

  const handleRemovePreviewSlot = (index: number) => {
    if (formData.dappPreviews.length > 4 && !formData.dappPreviews[index]) {
      setFormData((prev) => {
        const newPreviews = prev.dappPreviews.filter((_, i) => i !== index);
        return { ...prev, dappPreviews: newPreviews };
      });
    }
  };

  const handleSingleFileChange = useSingleFileHandler({
    onFileChange: (fieldName, file) => {
      setFormData((prev) => ({ ...prev, [fieldName]: file }));
    },
    onFormTouched: () => setIsFormTouched(true),
  });

  const createDappWithImages = async (validatedData: NewDappDataType) => {
    try {
      if (activePublisher?.isFrozen) {
        throw new Error(PUBLISHER_FROZEN_ACTION_TOOLTIP);
      }

      if (!publisherVerification.isApproved) {
        throw new Error(publisherVerification.actionMessage);
      }

      let dappIconUrl: string | undefined;
      let bannerUrl: string | undefined;
      let editorsChoiceGraphicUrl: string | undefined;
      const dappPreviewUrls: string[] = [];

      if (formData.dappIcon) {
        dappIconUrl = await uploadFile(formData.dappIcon, 'logos');
      }

      if (formData.banner) {
        bannerUrl = await uploadFile(formData.banner, 'banners');
      }

      if (formData.editorsChoiceGraphic) {
        editorsChoiceGraphicUrl = await uploadPortalMediaFile({
          trpc,
          file: formData.editorsChoiceGraphic,
          folder: 'previews',
          fileType: 'ReleaseFeatureGraphic',
          mode: 'image',
        });
      }

      for (const preview of formData.dappPreviews) {
        if (preview) {
          const previewUrl = await uploadFile(preview, 'previews');
          dappPreviewUrls.push(previewUrl);
        }
      }

      const formWalletAddress = validatedData.walletAddress?.trim();
      const connectedWalletAddress =
        wallet.connected && wallet.publicKey
          ? wallet.publicKey.toBase58()
          : undefined;
      const walletAddressForDapp =
        formWalletAddress || connectedWalletAddress || undefined;

      if (!walletAddressForDapp) {
        toast.error(
          'A wallet address is required to mint the App NFT. Connect a wallet or provide one in the form.',
        );
        throw new Error('Wallet address required');
      }

      const result = await trpc.createDapp.mutate({
        dappName: validatedData.dappName,
        packageName: validatedData.packageName,
        subtitle: validatedData.subtitle || undefined,
        description: validatedData.description,
        dappIconUrl,
        bannerUrl,
        featureGraphicUrl: undefined,
        dappPreviewUrls,
        languages: validatedData.languages,
        availableCountriesMode: validatedData.availableCountriesMode,
        availableCountries: validatedData.availableCountries,
        appWebsite: ensureHttpsUrl(validatedData.appWebsite || ''),
        contactEmail: validatedData.contactEmail,
        supportEmail: validatedData.supportEmail,
        tokenDistribution: validatedData.tokenDistribution,
        walletAddress: walletAddressForDapp,
        licenseUrl: ensureHttpsUrl(validatedData.licenseUrl || ''),
        privacyPolicyUrl: ensureHttpsUrl(validatedData.privacyPolicyUrl || ''),
        editorsChoiceHeadline: validatedData.editorsChoiceHeadline || undefined,
        editorsChoiceTag: validatedData.editorsChoiceTag || undefined,
        editorsChoiceGraphicUrl,
      });

      console.log('Dapp created successfully:', result);

      if (result._tag === 'Left') {
        throw new Error(result.left.message || 'Failed to create dApp');
      }

      const createdDapp = result.right;

      toast.success('dApp created successfully!', {
        description: `${validatedData.dappName} has been added to your dashboard.`,
      });

      window.dispatchEvent(new CustomEvent('dappListUpdated'));

      if (wallet.connected && wallet.publicKey) {
        const confirmedAppWalletAddress = createdDapp.walletAddress;

        if (!confirmedAppWalletAddress) {
          throw new Error('Expected app wallet address to be set on the dApp.');
        }

        console.log('Wallet connected, proceeding with App NFT minting...');

        setMintingAppNft(true);

        try {
          const appNftData = {
            dappId: createdDapp.id,
            dappName: createdDapp.dappName,
            dappIconUrl: createdDapp.dappIconUrl,
            appWebsite: createdDapp.appWebsite,
            packageName: createdDapp.packageName,
            walletAddress: confirmedAppWalletAddress,
          };

          toast.info('Starting App NFT minting process...', { duration: 5000 });

          if (!formData.dappIcon) {
            throw new Error(
              'App icon file is missing. Upload an icon before minting the App NFT.',
            );
          }

          const mintResult = await mintAppNft(
            wallet,
            appNftData,
            formData.dappIcon,
            trpc,
            uploadAppNftData,
            (status) => {
              if (status) {
                toast.info(status, { duration: 3000 });
              }
            },
          );

          if (mintResult.success) {
            toast.success('App NFT minted successfully!', {
              description: `Mint address: ${mintResult.mintAddress}`,
            });

            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            captureAndReportError('App NFT minting failed:', mintResult.error);

            const errorMessage = formatMintingErrorMessage(mintResult.error);
            toast.error('App NFT minting failed', {
              description: errorMessage,
              duration: 8000,
            });

            setCreatedDappData({
              id: createdDapp.id,
              name: createdDapp.dappName,
              iconUrl: createdDapp.dappIconUrl || undefined,
              website: createdDapp.appWebsite || undefined,
              androidPackage: createdDapp.packageName || '',
              publisherAddress: confirmedAppWalletAddress,
            });
            setShowAppNftModal(true);
          }
        } catch (error) {
          captureAndReportError(
            'Unexpected error during App NFT minting:',
            error,
          );

          setCreatedDappData({
            id: createdDapp.id,
            name: createdDapp.dappName,
            iconUrl: createdDapp.dappIconUrl || undefined,
            website: createdDapp.appWebsite || undefined,
            androidPackage: createdDapp.packageName || '',
            publisherAddress: confirmedAppWalletAddress,
          });
          setShowAppNftModal(true);
        } finally {
          setMintingAppNft(false);
        }
      } else {
        console.log('Wallet not connected, skipping App NFT minting');
        toast.info(
          'dApp created! Connect a wallet to mint the App NFT from the dashboard.',
          {
            duration: 5000,
          },
        );
        navigate('/dashboard');
      }

      return result;
    } catch (error) {
      captureAndReportError('Error creating dapp:', error);

      toast.error('Failed to create dApp', {
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
      });

      throw error;
    }
  };

  const handleSubmit = (
    input: TaskEither<TypedError<'InvalidFormData'>, NewDappDataType>,
  ): TaskEither<unknown, unknown> =>
    pipe(
      input,
      chainW((validatedData) => async () => {
        setIsSubmitting(true);
        try {
          const result = await createDappWithImages(validatedData);
          setIsSubmitting(false);
          return result;
        } catch (error) {
          setIsSubmitting(false);
          return errorToLeft(error, {
            fallbackMessage: 'Failed to create dApp',
            preserveErrorMessage: true,
            includeRaw: true,
          });
        }
      }),
      mapLeft((validationError) => {
        if (validationError.name === 'InvalidFormData') {
          captureAndReportError('Form validation failed:', validationError);
        }
        return validationError;
      }),
    );

  return (
    <div className="bg-[#161717] h-full flex flex-col">
      <NewDappPageHeader
        isFormTouched={isFormTouched}
        isFormValid={isFormValid}
        isSubmitting={isSubmitting || mintingAppNft}
      />
      <PublisherFrozenBanner />
      <div className="flex flex-col md:flex-row gap-8 p-8 bg-[#161717] text-white flex-1 overflow-y-auto">
        <div className="flex-1">
          <NewDappForm
            formData={formData}
            handleInputChange={handleInputChange}
            handlePreviewFileChange={handlePreviewFileChange}
            handleAddPreviewSlot={handleAddPreviewSlot}
            handleRemovePreviewSlot={handleRemovePreviewSlot}
            handleSingleFileChange={handleSingleFileChange}
            handleSubmit={handleSubmit}
            onValidityChange={setIsFormValid}
            previewDimensions={previewDimensions}
          />
        </div>
      </div>

      {createdDappData && (
        <AppNftMintModal
          isOpen={showAppNftModal}
          onClose={() => {
            setShowAppNftModal(false);
            setCreatedDappData(null);
            navigate('/dashboard');
          }}
          onSuccess={(mintAddress, txSignature) => {
            console.log('App NFT minted successfully:', {
              mintAddress,
              txSignature,
            });
            toast.success('App NFT minted successfully!', {
              description: `Mint address: ${mintAddress}`,
            });
            setShowAppNftModal(false);
            setCreatedDappData(null);
            navigate('/dashboard');
          }}
          appData={createdDappData}
        />
      )}
    </div>
  );
}
